import { useId, useState, type ReactNode } from "react";
import { ChevronDown, Info, Lightbulb } from "lucide-react";
import { FaqList, type FaqEntry } from "@/components/FaqSection";
import { ToolVideoEmbed } from "@/components/ToolVideoEmbed";
import { NextStepSuggestion } from "@/components/NextStepSuggestion";
import { GuideLinks } from "@/components/GuideLinks";
import type { ToolInfo } from "@/components/ToolInfoContext";
import type { ToolEditorialContent } from "@/lib/tool-editorial-content";
import { trackEvent } from "@/lib/analytics";
import { getToolVideo } from "@/lib/tool-videos";
import { getNextStepsForTool } from "@/lib/workflows-data";
import { guideLinksFor } from "@/lib/blog";
import { NoticeBox } from "@/components/NoticeBox";
import { Link } from "wouter";
import { useLocale } from "@/hooks/useLocale";

/**
 * The one layout every tool page uses below the tool, on phone and desktop:
 *
 *   About            |  Video            (side by side on desktop, stacked on phone)
 *   Common questions   (collapsed)
 *   Details            (collapsed: how to use, when to use, limits, privacy)
 *   Guides and next steps (collapsed)
 *
 * All text is in the page at all times; collapsed sections are only hidden
 * visually (the `hidden` attribute), so search engines and screen readers
 * still get it and the structured FAQ data matches what is on the page.
 */
export interface ToolInfoPanelProps {
  toolId: string;
  toolName: string;
  description: string;
  info: ToolInfo | null;
  faq?: FaqEntry[];
  editorial?: ToolEditorialContent;
  /** Extra detail blocks for tools that have their own content shape (multi-tool apps). */
  extraDetails?: ReactNode;
}

function Disclosure({
  toolId,
  section,
  title,
  children,
  defaultOpen = false,
}: {
  toolId: string;
  section: string;
  title: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  return (
    <div className="border-t border-border/60 first:border-t-0">
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            if (!open) trackEvent("tool_info_open", { tool_id: toolId, section });
            setOpen(!open);
          }}
          className="flex w-full min-h-[48px] items-center justify-between gap-3 py-3 text-left text-base font-semibold"
        >
          {title}
          <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
        </button>
      </h2>
      <div id={panelId} hidden={!open} className="pb-5">
        {children}
      </div>
    </div>
  );
}

function Para({ children }: { children: string }) {
  return <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{children}</p>;
}

function EditorialSections({ sections }: { sections: NonNullable<ToolEditorialContent["sections"]> }) {
  const { localizedHref } = useLocale();
  return (
    <>
      {sections.map((sec) => (
        <div key={sec.heading}>
          <h3 className="text-sm font-semibold mb-1">{sec.heading}</h3>
          <Para>{sec.body}</Para>
          {sec.table && (
            <div className="mt-2 overflow-x-auto" tabIndex={0} role="region" aria-label={sec.table.caption}>
              <table className="w-full text-sm border-collapse">
                <caption className="sr-only">{sec.table.caption}</caption>
                <thead><tr>{sec.table.head.map((h) => <th key={h} scope="col" className="text-left font-semibold border-b border-border/60 py-1.5 pr-3 align-top">{h}</th>)}</tr></thead>
                <tbody>{sec.table.rows.map((r) => (
                  <tr key={r[0]}>{r.map((c, i) => i === 0
                    ? <th key={i} scope="row" className="text-left font-medium border-b border-border/40 py-1.5 pr-3 align-top">{c}</th>
                    : <td key={i} className="text-muted-foreground border-b border-border/40 py-1.5 pr-3 align-top">{c}</td>)}</tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {sec.links && (
            <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {sec.links.map((l) => <li key={l.href}><Link href={localizedHref(l.href)} className="text-primary hover:underline">{l.label}</Link></li>)}
            </ul>
          )}
        </div>
      ))}
    </>
  );
}

export function ToolInfoPanel({ toolId, toolName, description, info, faq, editorial, extraDetails }: ToolInfoPanelProps) {
  const [howOpen, setHowOpen] = useState(false);
  const howId = useId();
  const about = info?.whatIsIt || description;
  const how = info?.howToUse;
  const hasVideo = !!getToolVideo(toolId);

  const details = editorial || extraDetails;
  const hasGuides = getNextStepsForTool(toolId).length > 0 || guideLinksFor("tools", toolId).length > 0;

  return (
    <div className="mt-8 space-y-6">
      <div className={`grid gap-6 items-start ${hasVideo ? "md:grid-cols-2" : "max-w-3xl"}`}>
        <section aria-label={`About ${toolName}`} className="min-w-0">
          <h2 className="text-base font-semibold mb-2">About {toolName}</h2>
          <Para>{about}</Para>
          {info?.disclaimer && (
            <NoticeBox className="mt-3 flex gap-2">
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
              <p className="text-sm">{info.disclaimer}</p>
            </NoticeBox>
          )}
          {(how || info?.tip) && (
            <>
              <button
                type="button"
                aria-expanded={howOpen}
                aria-controls={howId}
                onClick={() => {
                  if (!howOpen) trackEvent("tool_info_open", { tool_id: toolId, section: "how_to_use" });
                  setHowOpen(!howOpen);
                }}
                className="mt-3 inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary"
              >
                How to use it
                <ChevronDown className={`h-4 w-4 transition-transform ${howOpen ? "rotate-180" : ""}`} aria-hidden />
              </button>
              <div id={howId} hidden={!howOpen} className="space-y-3">
                {how && <Para>{how}</Para>}
                {info?.tip && (
                  <div className="flex gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
                    <Lightbulb className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
                    <p className="text-sm text-muted-foreground leading-relaxed">{info.tip}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
        <ToolVideoEmbed toolId={toolId} toolName={toolName} />
      </div>

      <div className="rounded-xl border border-border/60 px-4 bg-card/30">
        {faq && faq.length > 0 && (
          <Disclosure toolId={toolId} section="faq" title="Common questions">
            <FaqList entries={faq} />
          </Disclosure>
        )}
        {details && (
          <Disclosure toolId={toolId} section="details" title="Details: when to use it, limits, privacy">
            <div className="space-y-5 max-w-3xl">
              {editorial && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold mb-1">How to use {toolName}</h3>
                    {editorial.howToUse.split("\n\n").map((p, i) => (
                      <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-2 last:mb-0">{p}</p>
                    ))}
                  </div>
                  <div><h3 className="text-sm font-semibold mb-1">When to use it</h3><Para>{editorial.whenToUse}</Para></div>
                  <div><h3 className="text-sm font-semibold mb-1">When not to use it</h3><Para>{editorial.whenNotTo}</Para></div>
                  <div><h3 className="text-sm font-semibold mb-1">Tips for best results</h3><Para>{editorial.tips}</Para></div>
                  <div><h3 className="text-sm font-semibold mb-1">Limitations</h3><Para>{editorial.limitations}</Para></div>
                  <div><h3 className="text-sm font-semibold mb-1">Privacy</h3><Para>{editorial.privacyNote}</Para></div>
                  {editorial.sections && <EditorialSections sections={editorial.sections} />}
                </>
              )}
              {extraDetails}
            </div>
          </Disclosure>
        )}
        {hasGuides && (
          <Disclosure toolId={toolId} section="guides" title="Guides and next steps">
            <NextStepSuggestion toolId={toolId} />
            <GuideLinks kind="tools" id={toolId} variant="plain" />
          </Disclosure>
        )}
      </div>
    </div>
  );
}
